import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { ParsedClassroom } from "@examspark/types";

import { useMutationUpdateClassroom } from "../api/operations/classroom";
import {
  useMutationCreateClassroomUnit,
  useMutationDeleteClassroomUnit,
  useMutationGetClassroomUnit,
  useMutationUpdateClassroomUnit,
} from "../api/operations/classroom-unit";

import { AnalyticsEventName, useAnalytics } from "./useAnalytics";

export const useClassroomUnitsManager = (classroom?: ParsedClassroom) => {
  const analytics = useAnalytics();
  const [isStarting, setIsStarting] = useState(false);
  // const [isSkipping, setIsSkipping] = useState(false);
  // const [isFinishing, setIsFinishing] = useState(false);
  // const [isResetting, setIsResetting] = useState(false);

  const getClassroomUnitMutation = useMutationGetClassroomUnit();
  const createClassroomUnitMutation = useMutationCreateClassroomUnit();
  const updateClassroomUnitMutation = useMutationUpdateClassroomUnit();
  const deleteClassroomUnitMutation = useMutationDeleteClassroomUnit();
  const updateClassroomMutation = useMutationUpdateClassroom();
  const queryClient = useQueryClient();

  const handleStart = useCallback(
    async (unitId: string) => {
      if (!classroom) return;
      // patch: currently getClassroomUnit sends a 403 when it doens't exist,
      // which ruins the point of us trying to check if it exists here. So
      // let's catch the error and warn it until we can implement a neater fix
      let result;
      setIsStarting(true);
      try {
        result = await getClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
        });
      } catch (e: any) {
        console.warn(e);
      }

      if (result?.id) {
        await updateClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          workspaceId: classroom.workspaceId,
          schemaVersion: "2020-01-01",
          maturity: "IN_PROGRESS",
          startedAt: new Date().toISOString(),
        });
      } else {
        await createClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          workspaceId: classroom.workspaceId,
          schemaVersion: "2020-01-01",
          maturity: "IN_PROGRESS",
          startedAt: new Date().toISOString(),
        });
      }

      await updateClassroomMutation.mutateAsync({
        id: classroom.id,
        sk: `classroom`,
        workspaceId: classroom.workspaceId,
        schemaVersion: "2020-01-01",
        unitId,
      });

      setIsStarting(false);
      queryClient.invalidateQueries(["classroom", classroom.id]);
      queryClient.invalidateQueries(["classrooms"]);
      analytics.trackEvent(AnalyticsEventName.ClassroomUnitStart);
    },
    [
      analytics,
      classroom,
      createClassroomUnitMutation,
      getClassroomUnitMutation,
      queryClient,
      updateClassroomMutation,
      updateClassroomUnitMutation,
    ],
  );

  const handleSkip = useCallback(
    async (unitId: string) => {
      if (!classroom) return;
      // patch: currently getClassroomUnit sends a 403 when it doens't exist,
      // which ruins the point of us trying to check if it exists here. So
      // let's catch the error and warn it until we can implement a neater fix
      let result;
      try {
        result = await getClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
        });
      } catch (e: any) {
        console.warn(e);
      }

      if (result?.id) {
        await updateClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          maturity: "SKIPPED",
          completedAt: null,
          startedAt: null,
        });
      } else {
        await createClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          maturity: "SKIPPED",
          completedAt: null,
          startedAt: null,
        });
      }

      if (classroom.unitId === unitId) {
        await updateClassroomMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          unitId: null,
        });
      }

      queryClient.invalidateQueries(["classroom", classroom.id]);
      queryClient.invalidateQueries(["classrooms"]);
      analytics.trackEvent(AnalyticsEventName.ClassroomUnitSkip);
    },
    [
      analytics,
      classroom,
      createClassroomUnitMutation,
      getClassroomUnitMutation,
      queryClient,
      updateClassroomMutation,
      updateClassroomUnitMutation,
    ],
  );

  const handleFinish = useCallback(
    async (unitId: string) => {
      if (!classroom) return;

      // patch: currently getClassroomUnit sends a 403 when it doens't exist,
      // which ruins the point of us trying to check if it exists here. So
      // let's catch the error and warn it until we can implement a neater fix
      let result;
      try {
        result = await getClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
        });
      } catch (e: any) {
        console.warn(e);
      }

      if (result?.id) {
        await updateClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          maturity: "COMPLETE",
          completedAt: new Date().toISOString(),
        });
      } else {
        await createClassroomUnitMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom-unit#${unitId}`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          maturity: "COMPLETE",
          completedAt: null,
          startedAt: null,
        });
      }

      await updateClassroomMutation.mutateAsync({
        id: classroom.id,
        sk: `classroom`,
        schemaVersion: "2020-01-01",
        workspaceId: classroom.workspaceId,
        unitId: null,
      });

      queryClient.invalidateQueries(["classroom", classroom.id]);
      queryClient.invalidateQueries(["classrooms"]);
      analytics.trackEvent(AnalyticsEventName.ClassroomUnitFinish);
    },
    [
      analytics,
      classroom,
      createClassroomUnitMutation,
      getClassroomUnitMutation,
      queryClient,
      updateClassroomMutation,
      updateClassroomUnitMutation,
    ],
  );

  const handleReset = useCallback(
    async (unitId: string) => {
      if (!classroom) return;
      await deleteClassroomUnitMutation.mutateAsync({
        id: classroom.id,
        sk: `classroom-unit#${unitId}`,
      });

      if (classroom.unitId === unitId) {
        await updateClassroomMutation.mutateAsync({
          id: classroom.id,
          sk: `classroom`,
          schemaVersion: "2020-01-01",
          workspaceId: classroom.workspaceId,
          unitId: null,
        });
      }

      queryClient.invalidateQueries(["classroom", classroom.id]);
      queryClient.invalidateQueries(["classrooms"]);
      analytics.trackEvent(AnalyticsEventName.ClassroomUnitReset);
    },
    [
      analytics,
      classroom,
      deleteClassroomUnitMutation,
      queryClient,
      updateClassroomMutation,
    ],
  );

  return {
    handleStart,
    handleSkip,
    handleFinish,
    handleReset,
    isStarting,
    // isSkipping,
    // isResetting,
    // isFinishing,
  };
};
